import { Flex, Th, Tr, Button, Input, Box, Text } from '@chakra-ui/react';
import { AddIcon, CheckIcon } from '@chakra-ui/icons';
import { groupBy, isEmpty, isEqual } from 'lodash';
import { ConditionalComponent, IterationComponent } from '@components/functionComponent';
import { useEffect, useRef, useState } from 'react';
import SkeletonComponent from '@components/skeleton';

export default function AddItemComp({
    tableList,
    onOpen,
    isOpen,
    onClose,
    queryAddItem,
    isLoading,
    onSelectItem,
    onSearchItem,
    groupBy: groupByProp,
    editingRow,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    placeholder = 'Cari nama atau ID produk ...',
    addBtnLabel = 'Tambah Product',
}) {
    const [value, setValue] = useState(null);
    const [clicked, setClicked] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);
    const loadMoreRef = useRef(null);

    const handleSelect = () => {
        if (isEmpty(selectedItem)) return;
        onSelectItem(selectedItem);
        handleClose();
    };

    const handleClick = (value) => () => {
        setValue(value.name);
        setSelectedItem(value);
        setClicked(true);
    };

    const handleSearch = (e) => {
        setValue(e.target.value);
        onSearchItem(e.target.value);
        setClicked(false);
    };

    const handleClose = () => {
        setValue(null);
        setSelectedItem(null);
        onSearchItem(null);
        onClose();
    };

    const renderItem = (item) => {
        return (
            <Box
                p={'12px'}
                borderRadius={'4px'}
                transition={'.3s all ease'}
                _hover={{ bg: 'semantic.200' }}
                cursor={'pointer'}
                onClick={handleClick(item)}
                textTransform={'capitalize'}
            >
                <Text fontWeight={400} fontSize={'14px'}>
                    {item.name}
                </Text>
                <Text fontWeight={400} color={'neutral.600'} mt={'4px'}>
                    {item.id}
                </Text>
            </Box>
        );
    };

    useEffect(() => {
        if (editingRow) {
            setValue(editingRow.name);
            setSelectedItem(editingRow);
        }
    }, [editingRow]);

    useEffect(() => {
        if (!hasNextPage || !loadMoreRef.current) return;

        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting) {
                    fetchNextPage();
                }
            },
            { rootMargin: '200px' }
        );

        observer.observe(loadMoreRef.current);

        return () => {
            observer.disconnect();
        };
    }, [hasNextPage, fetchNextPage]);

    let groupedData = null;
    if (groupByProp) {
        groupedData = groupBy(
            queryAddItem,
            typeof groupByProp === 'function' ? groupByProp : (item) => item[groupByProp]
        );
    }

    return (
        <Tr>
            <Th colSpan={tableList?.length} p={'16px 12px'} position={'relative'}>
                <Button w={'full'} variant={'outline'} border={'1px dashed'} py={5} onClick={onOpen}>
                    <AddIcon fontSize={10} /> {addBtnLabel}
                </Button>

                {isOpen && (
                    <>
                        <Flex gap={'22px'} mt={'24px'}>
                            <Input value={value} placeholder={placeholder} onChange={handleSearch} />
                            <Flex gap={'12px'} alignItems={'center'}>
                                <Button onClick={handleSelect} size={'sm'} py={'18px'}>
                                    <CheckIcon />
                                </Button>
                                <Button onClick={handleClose} size={'sm'} py={'18px'} variant={'outline'}>
                                    <AddIcon transform="rotate(45deg)" />
                                </Button>
                            </Flex>
                        </Flex>

                        <ConditionalComponent statement={queryAddItem?.length > 0 && !isLoading && !clicked}>
                            <Box
                                p={'20px'}
                                borderRadius={'8px'}
                                boxShadow={'0px 1px 3px 0px #0D0A2C1A'}
                                position={'absolute'}
                                bg={'semantic.100'}
                                zIndex={999}
                                w={'calc(100% - 22px)'}
                                mt={'12px'}
                                maxH={'400px'}
                                overflowY={'scroll'}
                            >
                                {groupByProp
                                    ? Object.entries(groupedData).map(([groupName, items]) =>
                                          !isEmpty(items) ? (
                                              <Box mb="16px" key={groupName}>
                                                  <Text fontWeight={500} color="neutral.600" mb="8px">
                                                      {groupName}
                                                  </Text>
                                                  {items.map(renderItem)}
                                              </Box>
                                          ) : null
                                      )
                                    : queryAddItem.map(renderItem)}

                                {hasNextPage && (
                                    <Box ref={loadMoreRef}>
                                        <ConditionalComponent statement={isFetchingNextPage}>
                                            <IterationComponent
                                                arr={[1, 2, 3]}
                                                render={() => (
                                                    <Box bg={'semantic.200'} mb={'8px'} p={'12px'} borderRadius={'8px'}>
                                                        <SkeletonComponent
                                                            width="100%"
                                                            maxWidth={'50%'}
                                                            height={'20px'}
                                                            mb={'4px'}
                                                        />
                                                        <SkeletonComponent
                                                            width="100%"
                                                            maxWidth={'25%'}
                                                            height={'17px'}
                                                        />
                                                    </Box>
                                                )}
                                            />
                                        </ConditionalComponent>
                                    </Box>
                                )}
                            </Box>
                        </ConditionalComponent>

                        <ConditionalComponent statement={isLoading}>
                            <Box
                                p={'20px'}
                                borderRadius={'8px'}
                                boxShadow={'0px 1px 3px 0px #0D0A2C1A'}
                                position={'absolute'}
                                bg={'semantic.100'}
                                zIndex={999}
                                w={'calc(100% - 22px)'}
                                mt={'12px'}
                                maxH={'300px'}
                            >
                                <IterationComponent
                                    arr={[1, 2, 3]}
                                    render={() => (
                                        <Box bg={'semantic.200'} mb={'8px'} p={'12px'} borderRadius={'8px'}>
                                            <SkeletonComponent
                                                width="100%"
                                                maxWidth={'50%'}
                                                height={'20px'}
                                                mb={'4px'}
                                            />
                                            <SkeletonComponent width="100%" maxWidth={'25%'} height={'17px'} />
                                        </Box>
                                    )}
                                />{' '}
                            </Box>
                        </ConditionalComponent>

                        <ConditionalComponent statement={!isLoading && isEmpty(queryAddItem) && !isEmpty(value)}>
                            <Box
                                p={'20px'}
                                borderRadius={'8px'}
                                boxShadow={'0px 1px 3px 0px #0D0A2C1A'}
                                position={'absolute'}
                                bg={'semantic.100'}
                                zIndex={999}
                                w={'calc(100% - 22px)'}
                                mt={'12px'}
                                maxH={'300px'}
                                textTransform={'initial'}
                            >
                                <Text>"{value}" tidak dapat ditemukan</Text>
                            </Box>
                        </ConditionalComponent>
                    </>
                )}
            </Th>
        </Tr>
    );
}
